import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const STATUS_BADGE = {
  VERIFIED: { cls: "bg-success", label: "Verified" },
  MISSING: { cls: "bg-danger", label: "Missing" },
  DISCREPANCY: { cls: "bg-warning text-dark", label: "Discrepancy" },
  PENDING: { cls: "bg-secondary", label: "Pending" },
};

const AUDIT_STATUS_BADGE = {
  COMPLETED: "bg-success",
  ONGOING: "bg-warning text-dark",
  PENDING: "bg-secondary",
};

function ScanModal({ audit, token, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [scanning, setScanning] = useState(null);
  const [formState, setFormState] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-audits/${audit.id}/items`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [audit.id, token]);

  useEffect(() => { load(); }, [load]);

  const openForm = item => {
    setFormState(prev => ({
      ...prev,
      [item.id]: prev[item.id] || { status: "VERIFIED", physicalCondition: "Good", actualLocation: item.expectedLocation || "", remarks: "" }
    }));
  };

  const submitScan = async itemId => {
    const f = formState[itemId];
    if (!f?.status) return;
    setScanning(itemId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-audits/${audit.id}/items/${itemId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      if (!res.ok) throw new Error();
      toast.success("Item scanned.");
      await load();
      onDone();
    } catch { toast.error("Failed to save scan result."); }
    finally { setScanning(null); }
  };

  const total = items.length;
  const verified = items.filter(i => i.status === "VERIFIED").length;
  const missing = items.filter(i => i.status === "MISSING").length;
  const discrepancy = items.filter(i => i.status === "DISCREPANCY").length;
  const pending = items.filter(i => i.status === "PENDING").length;
  const scanned = total - pending;
  const pct = total > 0 ? Math.round((scanned / total) * 100) : 0;
  const filtered = filter === "ALL" ? items : items.filter(i => i.status === filter);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1060, width: "min(960px,95vw)", maxHeight: "90vh", background: "#fff", borderRadius: 12, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ background: "#1e3a5f", color: "#fff", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}><i className="bi bi-clipboard2-check me-2"></i>{audit.auditName}</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{audit.auditPeriod} · {audit.department || "All Depts"} · {audit.location || "All Locations"}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "12px 24px 0", flexShrink: 0 }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Progress: {scanned} / {total} scanned ({pct}%)</span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>{pending} remaining</span>
          </div>
          <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#16a34a" : "#2d5a9e", borderRadius: 4, transition: "width 0.3s" }} />
          </div>
          <div className="d-flex gap-2 mt-2 mb-2 flex-wrap">
            {[["ALL", `All (${total})`, "bg-light text-dark border"], ["PENDING", `Pending (${pending})`, "bg-secondary text-white"],
            ["VERIFIED", `Verified (${verified})`, "bg-success text-white"], ["MISSING", `Missing (${missing})`, "bg-danger text-white"],
            ["DISCREPANCY", `Discrepancy (${discrepancy})`, "bg-warning text-dark"]].map(([key, label, cls]) => (
              <button key={key} onClick={() => setFilter(key)}
                className={`badge border-0 px-3 py-2 ${cls}`}
                style={{ cursor: "pointer", fontSize: 12, fontWeight: filter === key ? 700 : 400, outline: filter === key ? "2px solid #1e3a5f" : "none" }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1, padding: "0 24px 24px" }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : filtered.length === 0 ? (
            <p className="text-muted text-center py-4">No items in this filter.</p>
          ) : filtered.map(item => {
            const f = formState[item.id];
            const isPending = item.status === "PENDING";
            return (
              <div key={item.id} className="border rounded mb-2 p-3"
                style={{ background: isPending ? "#fff" : item.status === "VERIFIED" ? "#f0fdf4" : item.status === "MISSING" ? "#fff1f2" : "#fffbeb" }}>
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                  <div style={{ flex: 1 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontFamily: "monospace" }}>{item.assetCode}</span>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{item.assetName}</span>
                      <span className={`badge ${STATUS_BADGE[item.status]?.cls || "bg-secondary"}`} style={{ fontSize: 11 }}>{STATUS_BADGE[item.status]?.label || item.status}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      Dept: <strong>{item.expectedDepartment || "—"}</strong> &nbsp;·&nbsp;
                      Location: <strong>{item.expectedLocation || "—"}</strong> &nbsp;·&nbsp;
                      Assigned: <strong>{item.assignedToName || "Unassigned"}</strong>
                    </div>
                    {item.scannedAt && (
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                        Scanned at {new Date(item.scannedAt).toLocaleString()}
                        {item.physicalCondition && ` · Condition: ${item.physicalCondition}`}
                        {item.actualLocation && ` · Found at: ${item.actualLocation}`}
                        {item.remarks && ` · Remarks: ${item.remarks}`}
                      </div>
                    )}
                  </div>
                  {isPending && !f && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openForm(item)}>
                      <i className="bi bi-search me-1"></i>Scan
                    </button>
                  )}
                  {isPending && f && (
                    <button className="btn btn-sm btn-outline-secondary"
                      onClick={() => setFormState(prev => { const n = { ...prev }; delete n[item.id]; return n; })}>
                      Cancel
                    </button>
                  )}
                </div>
                {isPending && f && (
                  <div className="mt-3 pt-3 border-top">
                    <div className="row g-2">
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: 12 }}>Result *</label>
                        <select className="form-select form-select-sm" value={f.status}
                          onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], status: e.target.value } }))}>
                          <option value="VERIFIED">✓ Verified</option>
                          <option value="MISSING">✗ Missing</option>
                          <option value="DISCREPANCY">⚠ Discrepancy</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: 12 }}>Physical Condition</label>
                        <select className="form-select form-select-sm" value={f.physicalCondition}
                          onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], physicalCondition: e.target.value } }))}>
                          <option>Good</option><option>Fair</option><option>Damaged</option><option>Not Found</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: 12 }}>Actual Location</label>
                        <input className="form-control form-control-sm" value={f.actualLocation}
                          onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], actualLocation: e.target.value } }))}
                          placeholder="Where found" />
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <button className="btn btn-sm btn-primary " disabled={scanning === item.id} onClick={() => submitScan(item.id)}>
                          {scanning === item.id ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : "Save"}
                        </button>
                      </div>
                      {(f.status === "MISSING" || f.status === "DISCREPANCY") && (
                        <div className="col-12">
                          <label className="form-label" style={{ fontSize: 12 }}>Remarks *</label>
                          <textarea className="form-control form-control-sm" rows={2} value={f.remarks}
                            onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], remarks: e.target.value } }))}
                            placeholder="Describe the issue..." />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default function AllAuditRecords() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanAudit, setScanAudit] = useState(null);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  // useEffect(() => {
  //   const roles = JSON.parse(localStorage.getItem("roles") || "[]");
  //   if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
  //   if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  // }, [navigate, token]);

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-audits?page=0&size=100`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAudits(Array.isArray(data) ? data : data.content || []);
    } catch { setAudits([]); toast.error("Failed to load audit records."); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchAudits(); }, [fetchAudits]);

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const res = await fetch(`${API_BASE_URL}/api/asset-audits/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = search ? `Audit_Records_Filtered_${date}.xlsx` : `Audit_Records_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const filtered = audits.filter(a => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (a.auditName || "").toLowerCase().includes(q) ||
      (a.department || "").toLowerCase().includes(q) ||
      (a.location || "").toLowerCase().includes(q) ||
      (a.auditPeriod || "").toLowerCase().includes(q);
  });

  const completed = audits.filter(a => a.status === "COMPLETED").length;
  const ongoing = audits.filter(a => a.status === "ONGOING").length;
  const pending = audits.filter(a => !a.status || a.status === "PENDING").length;

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-clipboard2-check me-2"></i> All Audit Records</h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>View verification cycles and scan assets to track presence, condition, and discrepancies.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate("/newauditrecord")}>
              <i className="bi bi-plus-circle me-1"></i> New Audit
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allassets")}>
              <i className="bi bi-box-seam me-1"></i> Asset List
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadReport} disabled={downloading}>
              <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Total Audits", value: audits.length, icon: "bi-clipboard2-check", color: "#1e3a5f" },
            { label: "Completed", value: completed, icon: "bi-check-circle", color: "#198754" },
            { label: "Ongoing", value: ongoing, icon: "bi-gear", color: "#fd7e14" },
            { label: "Pending", value: pending, icon: "bi-hourglass", color: "#6b7280" },
          ].map(item => (
            <div className="col-md-3 col-6" key={item.label}>
              <div className="bg-white border rounded p-2 d-flex align-items-center gap-2">
                <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 20 }}></i>
                <div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 18, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(p => !p)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>
        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by audit name, department, location..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setSearch("")}>Clear</button>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
          <span className="text-muted" style={{ fontSize: 13 }}>{filtered.length} audit{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading audit records...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-clipboard2-check" style={{ fontSize: 32, opacity: 0.3 }}></i>
            <div className="mt-2">No audit sessions found.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm align-middle text-start">
              <thead className="table-light">
                <tr>
                  <th>View | Edit</th>
                  <th>Audit Name</th><th>Period</th><th>Dept</th><th>Location</th>
                  <th>Status</th><th>Progress</th><th>Results</th><th>Created</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const total = a.totalItems || 0;
                  const scanned = total - (a.pendingItems || 0);
                  const pct = total > 0 ? Math.round((scanned / total) * 100) : 0;
                  return (
                    <tr key={a.id}>

                      <td>
                        <Link to={`/viewauditrecord/${a.id}`} className="me-1 text-primary" style={{ fontSize: 16 }}>View</Link>|
                        <Link to={`/editauditrecord/${a.id}`} className="ms-1 text-primary" style={{ fontSize: 16 }}>Edit</Link>
                      </td>
                      <td style={{ fontWeight: 600 }}>{a.auditName || "—"}</td>
                      <td><span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{a.auditPeriod}</span></td>
                      <td style={{ fontSize: 13 }}>{a.department || <span className="text-muted">All</span>}</td>
                      <td style={{ fontSize: 13 }}>{a.location || <span className="text-muted">All</span>}</td>
                      <td>
                        <span className={`badge ${AUDIT_STATUS_BADGE[a.status] || "bg-secondary"}`} style={{ fontSize: 11 }}>
                          {a.status || "PENDING"}
                        </span>
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#16a34a" : "#2d5a9e", borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>{scanned}/{total}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <span className="text-success me-2">✓ {a.verifiedItems || 0}</span>
                        <span className="text-danger me-2">✗ {a.missingItems || 0}</span>
                        <span className="text-warning">⚠ {a.discrepancyItems || 0}</span>
                      </td>
                      <td style={{ fontSize: 12, color: "#6b7280" }}>
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td>
                        {a.status !== "COMPLETED" && (
                          <button className="btn btn-sm btn-primary" onClick={() => setScanAudit(a)}>
                            <i className="bi bi-search me-1"></i>{a.status === "PENDING" ? "Start" : "Continue"}
                          </button>
                        )}
                        {a.status === "COMPLETED" && (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setScanAudit(a)}>
                            <i className="bi bi-eye me-1"></i>View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {scanAudit && (
        <ScanModal audit={scanAudit} token={token} onClose={() => setScanAudit(null)} onDone={fetchAudits} />
      )}
    </>
  );
}
